import React from "react";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { Box, createStyles, Menu } from "@mantine/core";
import { IconV3, RpiLabelText } from "@rpi/openapi-core";
import { tokens } from "@blurb/codex-react";
import { useNavBarButtonStyles } from "./NavBarStyles";
import { featureFlags } from "../../featureFlags";
import { useAuthUser } from "../../hooks/useAuthUser";
import { PublicUrl } from "../../utils/link.util";
import { useMenuStyles } from "../../components/menuStyles";

// With Developer Tools on, link straight to the new tab (no redirect hop) under its new name.
const apiKeysMenuItem = featureFlags.developerTools
  ? { label: "API Keys", route: "developer-tools:api-keys" as const }
  : { label: "API Credentials", route: "api-credentials" as const };

const useStyles = createStyles(() => ({
  navBarDevButton: {
    border: "none",
    cursor: "pointer",
    transition: "width 0.3s ease, padding 0.3s ease",
    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.primarySubtlest,
    },
  },
}));

export const NavBarDevToolsButton: React.FC = () => {
  const { classes: sharedClasses, cx } = useNavBarButtonStyles();
  const { classes: menuClasses } = useMenuStyles();
  const { classes } = useStyles();
  const navigateRoute = useNavigateRoute();
  const [opened, setOpened] = React.useState(false);
  const user = useAuthUser();

  const handleMenuItemClick = (callback: () => void) => {
    callback();
    setOpened(false);
  };

  return (
    <Menu
      position="right-start"
      offset={8}
      opened={opened}
      onChange={setOpened}
      withinPortal={true}
      classNames={menuClasses}
    >
      <Menu.Target>
        <Box
          className={cx(sharedClasses.navButton, classes.navBarDevButton)}
          component="button"
          aria-label="Developer Tools menu"
          aria-expanded={opened}
          aria-haspopup="menu"
          tabIndex={0}
        >
          <IconV3 icon="key_vertical" size={20} color="currentColor" aria-hidden="true" />
          <span className={sharedClasses.navButtonText}>
            <RpiLabelText>Developer Tools</RpiLabelText>
          </span>
        </Box>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconV3 icon="key" size={20} aria-hidden="true" />}
          onClick={() => handleMenuItemClick(() => navigateRoute([apiKeysMenuItem.route]))}
          role="menuitem"
        >
          {apiKeysMenuItem.label}
        </Menu.Item>
        <Menu.Item
          icon={<IconV3 icon="integration_instructions" size={20} aria-hidden="true" />}
          onClick={() => handleMenuItemClick(() => navigateRoute(["api-examples"]))}
          role="menuitem"
        >
          API Examples
        </Menu.Item>
        <Menu.Item
          component="a"
          href={PublicUrl.DOCS_HOME}
          target="_blank"
          rel="noopener noreferrer"
          icon={<IconV3 icon="open_in_new" size={20} aria-hidden="true" />}
          role="menuitem"
        >
          API Documentation
        </Menu.Item>
        {user.is.SUPER_ADMIN && (
          <Menu.Item
            icon={<IconV3 icon="lock" size={20} aria-hidden="true" />}
            onClick={() => handleMenuItemClick(() => navigateRoute(["configuration"]))}
            role="menuitem"
          >
            Configuration
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
